import React, { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, TrendingUp, Filter, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface PreferenceStats {
  wants: {
    label: string;
    count: number;
    percentage: number;
  }[];
  dealbreakers: {
    label: string;
    count: number;
    percentage: number;
    severity: 'absolute' | 'flexible';
  }[];
}

export function RoommatePreferences() {
  const [stats, setStats] = useState<PreferenceStats>({
    wants: [],
    dealbreakers: []
  });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'wants' | 'dealbreakers'>('wants');

  useEffect(() => {
    analyzePreferences();
  }, []);

  const analyzePreferences = async () => {
    try {
      const { data: preferences, error } = await supabase
        .from('user_preferences')
        .select('preferences, raw_description')
        .eq('type', 'roommate')
        .not('preferences', 'is', null);

      if (error) throw error;

      // Analyze wants
      const wantPatterns = {
        'Clean & Tidy': /\b(clean|tidy|neat|organized)\b/i,
        'Quiet': /\b(quiet|peaceful|calm)\b/i,
        'Professional': /\b(professional|career|working)\b/i,
        'Social': /\b(social|friendly|outgoing)\b/i,
        'Respectful': /\b(respect|considerate|mindful)\b/i,
        'Non-smoker': /\b(non.?smok|no.?smok)\b/i,
        'Pet-friendly': /\b(pet.?friendly|dog|cat)\b/i,
        'Early Riser': /\b(early.?ris|morning.?person)\b/i,
        'Night Owl': /\b(night.?owl|late.?night)\b/i,
        'Active Lifestyle': /\b(active|fitness|gym|workout)\b/i
      };

      const wants: Record<string, number> = {};
      const dealbreakers: Record<string, { count: number; severity: 'absolute' | 'flexible' }> = {};

      // Analyze each preference
      preferences.forEach(pref => {
        // Analyze wants from raw description
        Object.entries(wantPatterns).forEach(([trait, pattern]) => {
          if (pattern.test(pref.raw_description)) {
            wants[trait] = (wants[trait] || 0) + 1;
          }
        });

        // Analyze dealbreakers from structured data
        const dealbreakersArray = pref.preferences?.dealbreakers || [];
        dealbreakersArray.forEach((d: any) => {
          const key = d.type;
          if (!dealbreakers[key]) {
            dealbreakers[key] = { count: 0, severity: d.severity };
          }
          dealbreakers[key].count++;
        });
      });

      // Convert to sorted arrays
      const totalUsers = preferences.length;
      const wantsArray = Object.entries(wants)
        .map(([label, count]) => ({
          label,
          count,
          percentage: Math.round((count / totalUsers) * 100)
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      const dealbreakersArray = Object.entries(dealbreakers)
        .map(([label, { count, severity }]) => ({
          label,
          count,
          percentage: Math.round((count / totalUsers) * 100),
          severity
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      setStats({
        wants: wantsArray,
        dealbreakers: dealbreakersArray
      });
    } catch (error) {
      console.error('Error analyzing preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">Roommate Preferences Analysis</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setView('wants')}
            className={`px-3 py-1 rounded-full text-sm ${
              view === 'wants'
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center gap-1">
              <ThumbsUp className="w-4 h-4" />
              <span>Wants</span>
            </div>
          </button>
          <button
            onClick={() => setView('dealbreakers')}
            className={`px-3 py-1 rounded-full text-sm ${
              view === 'dealbreakers'
                ? 'bg-red-100 text-red-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <div className="flex items-center gap-1">
              <ThumbsDown className="w-4 h-4" />
              <span>Dealbreakers</span>
            </div>
          </button>
        </div>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={view === 'wants' ? stats.wants : stats.dealbreakers}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" unit="%" />
            <YAxis dataKey="label" type="category" width={100} />
            <Tooltip
              formatter={(value: number, name: string) => [`${value}%`, name]}
              contentStyle={{ backgroundColor: 'white', border: '1px solid #ddd' }}
            />
            <Bar
              dataKey="percentage"
              fill={view === 'wants' ? '#4a9c4f' : '#ef4444'}
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-6">
        <h3 className="text-lg font-medium mb-4">
          Top 5 {view === 'wants' ? 'Desired Traits' : 'Dealbreakers'}
        </h3>
        <div className="space-y-3">
          {(view === 'wants' ? stats.wants : stats.dealbreakers).map((item, index) => (
            <div
              key={item.label}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg font-medium text-gray-500">#{index + 1}</span>
                <span className="font-medium">{item.label}</span>
                {view === 'dealbreakers' && (
                  <span
                    className={`text-sm px-2 py-0.5 rounded-full ${
                      item.severity === 'absolute'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {item.severity}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className={`w-4 h-4 ${
                  view === 'wants' ? 'text-green-500' : 'text-red-500'
                }`} />
                <span className="font-medium">{item.percentage}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}